import { useQuery } from '@apollo/client';
import { IonRouterLink, IonText } from '@ionic/react';
import { MouseEvent, useCallback, useRef } from 'react';
import { FormattedMessage } from 'react-intl';

import ClientAvatar from '@components/ClientAvatar';
import styles from '@components/EverpresentTags.module.css';
import ExpertAvatar from '@components/ExpertAvatar';
import JourneyTagsForm from '@components/forms/JourneyTagsForm';
import HandleQuery from '@components/HandleQuery';
import Icon from '@components/text/Icon';
import useGlobalOpener from '@hooks/useGlobalOpener';
import useOpener from '@hooks/useOpener';
import {
  GetJourneyWithTagsDocument,
  GetJourneyWithTagsQuery,
  GetJourneyWithTagsQueryVariables
} from '@typing/Generated';
import { OpenerType } from '@typing/State';
import { clickCurrentTarget, handleTrapFocus } from '@utils/keyboardUtils';
import { expertRolesWithoutBackups } from '@utils/modelUtils';

type Props = {
  journeyId: string;
};

const EverpresentTags = ({ journeyId }: Props) => {
  const messagesOpener = useGlobalOpener(OpenerType.MESSAGES);
  const tagsOpener = useGlobalOpener(OpenerType.TAGS);
  const tasksOpener = useGlobalOpener(OpenerType.TASKS);
  const tagsExpandOpener = useOpener();

  const messagesIsOpen = messagesOpener.isOpen;
  const tasksIsOpen = tasksOpener.isOpen;
  const tagsIsOpen = tagsOpener.isOpen;
  const queryParams = new URLSearchParams(location.search);

  const hideFab = queryParams.has('hideFab');

  const tagsRef = useRef<HTMLDivElement>(null);

  const getJourneyTagsQuery = useQuery<GetJourneyWithTagsQuery, GetJourneyWithTagsQueryVariables>(
    GetJourneyWithTagsDocument,
    {
      variables: { id: journeyId }
    }
  );

  const handleOpenTags = useCallback(() => {
    tagsOpener.open();
  }, [tagsOpener]);

  const handleCloseTags = useCallback(() => {
    tagsExpandOpener.close();
    // expertTaskOpener.close();
    tagsOpener.close();
  }, [tagsExpandOpener, tagsOpener]);

  const handleExpandToggle = useCallback(
    (event: MouseEvent<HTMLIonRouterLinkElement>) => {
      event.stopPropagation();
      event.preventDefault();
      tagsExpandOpener.toggle();
    },
    [tagsExpandOpener]
  );

  if (hideFab) {
    return <></>;
  }

  return (
    <>
      <HandleQuery hideLoader query={getJourneyTagsQuery}>
        {journeyData => (
          <>
            <div
              className={`${tagsIsOpen ? 'ion-hide' : styles.closed} ${
                messagesIsOpen && tasksIsOpen
                  ? styles.messagesTasksOpen
                  : messagesIsOpen
                  ? styles.messagesOpen
                  : tasksIsOpen
                  ? styles.tasksOpen
                  : ''
              }`}
              onClick={tagsOpener.open}
            >
              <div className={styles.iconWithUnread}>
                <Icon ariaLabelKey="accessibility.ariaLabels.clipboard" className={styles.tagsIconClosed} icon="tags" />
              </div>
            </div>
            <div
              className={`${styles.everpresent} ${tagsExpandOpener.isOpen ? styles.fullScreenEverpresent : ''} ${
                tagsIsOpen ? '' : 'ion-hide'
              } ${
                messagesIsOpen && tasksIsOpen
                  ? styles.messagesTasksOpen
                  : messagesIsOpen
                  ? styles.messagesOpen
                  : tasksIsOpen
                  ? styles.tasksOpen
                  : ''
              }`}
              data-test="tagging"
              onKeyUp={event => {
                handleTrapFocus(event, tagsRef, handleCloseTags);
              }}
              ref={tagsRef}
            >
              <div
                className={`${tagsIsOpen ? styles.header : styles.headerWhite} ${
                  tagsExpandOpener.isOpen ? styles.fullScreenHeader : tagsIsOpen ? styles.expandedHeader : ''
                }`}
                data-test="tags-header"
                onClick={!tagsIsOpen ? handleOpenTags : handleCloseTags}
                role="complementary"
              >
                <div className={styles.openerIcon}>
                  <Icon ariaLabelKey="accessibility.ariaLabels.clipboard" className={styles.tagsIcon} icon="tags" />
                </div>
                <IonText>
                  <FormattedMessage id="models.journey.tagListLabel" />
                </IonText>
                <div className={styles.icons}>
                  {tagsIsOpen && (
                    <>
                      {expertRolesWithoutBackups(journeyData.journey.expertRoles)?.map(role => {
                        if (role.expert.photoUrl) {
                          return (
                            <div key={role.id}>
                              <ExpertAvatar expert={role.expert} size={30} />
                            </div>
                          );
                        }
                        return null;
                      })}
                      {journeyData.journey.clientRoles.map(role => (
                        <div key={role.id}>
                          <ClientAvatar
                            clientId={role.client.id}
                            color="primary700"
                            firstName={role.client.firstName}
                            journeyId={journeyId}
                            lastName={role.client.lastName}
                            size={30}
                          />
                        </div>
                      ))}
                      <div className={styles.toggleExpand}>
                        <IonRouterLink onClick={handleExpandToggle} onKeyUp={clickCurrentTarget} tabIndex={0}>
                          <Icon
                            ariaLabelKey="accessibility.ariaLabels.toggleTags"
                            className={styles.toggleIcon}
                            icon={tagsExpandOpener.isOpen ? 'compress' : 'expand'}
                          />
                        </IonRouterLink>
                      </div>
                    </>
                  )}
                  <div className={styles.toggleExpand}>
                    <IonRouterLink
                      onClick={tasksIsOpen ? handleCloseTags : undefined}
                      onKeyUp={clickCurrentTarget}
                      tabIndex={0}
                    >
                      <Icon
                        ariaLabelKey="accessibility.ariaLabels.toggleTags"
                        className={tagsIsOpen ? styles.toggleIcon : styles.toggleIconWhite}
                        icon={tagsIsOpen ? 'chevronDown' : 'chevronUp'}
                      />
                    </IonRouterLink>
                  </div>
                </div>
              </div>
              <div
                className={
                  tagsExpandOpener.isOpen ? styles.fullScreenContent : tagsIsOpen ? styles.expandedContent : 'ion-hide'
                }
              >
                <JourneyTagsForm
                  journey={journeyData.journey}
                  onError={tagsExpandOpener.close}
                  onSuccess={tagsExpandOpener.close}
                />
              </div>
            </div>
          </>
        )}
      </HandleQuery>
    </>
  );
};

export default EverpresentTags;
